import { useState, useCallback } from 'react';
import instance from '../../index';
import { ClientRanking, UserClient, RankingFilter } from '../../../models/client-ranking';
import { BaseResponse } from '../../../models/base-response';
import parseBlobError from '../../parse-blob-error';

interface UseRankingServiceReturn {
  getClientRanking: (clientId: string, filter?: RankingFilter) => Promise<ClientRanking | null>;
  getUserClient: () => Promise<UserClient | null>;
  getDashboardExcel: (
    clientId: string,
    filter?: Pick<RankingFilter, 'inicioIntervalo' | 'fimIntervalo'>
  ) => Promise<Blob>;
  loading: boolean;
  error: string | null;
}

export const useRankingService = (): UseRankingServiceReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getClientRanking = useCallback(async (
    clientId: string, 
    filter?: RankingFilter
  ): Promise<ClientRanking | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      // Converter para os nomes de parâmetros do novo endpoint
      if (filter?.inicioIntervalo) params.append('startDate', filter.inicioIntervalo);
      if (filter?.fimIntervalo) params.append('endDate', filter.fimIntervalo);
      if (filter?.topCount) params.append('topCount', filter.topCount.toString());
      
      // Usar o novo endpoint otimizado
      const response = await instance.get<BaseResponse<ClientRanking>>(
        `/v1/backoffice/performance-history/client/${clientId}/dashboard-optimized?${params.toString()}`
      );
      
      return response.data?.data || null;
    } catch (err: any) {
      const message = err.response?.data?.message || 'Erro ao carregar ranking';
      setError(message);
      // Propagar falha: a Home não deve tratar null como “usar mock”.
      throw err instanceof Error ? err : Object.assign(new Error(message), { response: err?.response, code: err?.code });
    } finally {
      setLoading(false);
    }
  }, []);

  const getUserClient = useCallback(async (): Promise<UserClient | null> => {
    try {
      const response = await instance.get<BaseResponse<UserClient>>('/v1/backoffice/backoffice-users/me/client');
      return response.data?.data || null;
    } catch (err: any) {
      console.error('Erro ao buscar cliente do usuário:', err);
      return null;
    }
  }, []);

  const getDashboardExcel = useCallback(async (
    clientId: string,
    filter?: Pick<RankingFilter, 'inicioIntervalo' | 'fimIntervalo'>
  ): Promise<Blob> => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filter?.inicioIntervalo) params.append('startDate', filter.inicioIntervalo);
      if (filter?.fimIntervalo) params.append('endDate', filter.fimIntervalo);

      const response = await instance.get(
        `/v1/backoffice/performance-history/client/${clientId}/dashboard/excel?${params.toString()}`,
        { responseType: 'blob' }
      );
      return response.data;
    } catch (err: any) {
      try {
        await parseBlobError(err, 'Erro ao exportar dashboard');
      } catch (parsed: any) {
        setError(parsed?.message || 'Erro ao exportar dashboard');
        throw parsed;
      }
      // parseBlobError deve sempre throw; se não, mantém Promise<Blob> rejeitada
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    getClientRanking,
    getUserClient,
    getDashboardExcel,
    loading,
    error
  };
};

// ========================================
// DADOS MOCK — apenas testes / Storybook.
// NÃO usar na Home em runtime (mascara 403/rede).
// ========================================

export const getMockRankingData = (): ClientRanking => ({
  clientId: '1',
  clientName: 'Transportadora ABC',
  topDriversInertia: [
    { position: 1, driverName: 'Carlos Silva', driverCpf: '12345678900', driverId: '1', plate: 'ABC-1234', inertiaPercentage: 94.2, motorLoadPercentage: 93.4, inertiaPrize: 850, engineLoadPrize: 780, odometro: 4520, fleetName: 'Frota 1' },
    { position: 2, driverName: 'José Santos', driverCpf: '98765432100', driverId: '2', plate: 'DEF-5678', inertiaPercentage: 91.8, motorLoadPercentage: 89.2, inertiaPrize: 720, engineLoadPrize: 610, odometro: 3890, fleetName: 'Frota 1' },
    { position: 3, driverName: 'Pedro Oliveira', driverCpf: '45678912300', driverId: '3', plate: 'GHI-9012', inertiaPercentage: 89.5, motorLoadPercentage: 88.1, inertiaPrize: 650, engineLoadPrize: 590, odometro: 4100, fleetName: 'Frota 2' },
    { position: 4, driverName: 'Antonio Lima', driverCpf: '32165498700', driverId: '4', plate: 'JKL-3456', inertiaPercentage: 87.3, motorLoadPercentage: 85.7, inertiaPrize: 580, engineLoadPrize: 520, odometro: 3750, fleetName: 'Frota 1' },
    { position: 5, driverName: 'Marcos Souza', driverCpf: '65432178900', driverId: '5', plate: 'MNO-7890', inertiaPercentage: 85.1, motorLoadPercentage: 84.2, inertiaPrize: 520, engineLoadPrize: 480, odometro: 3200, fleetName: 'Frota 2' },
  ],
  worstDriversInertia: [
    { position: 1, driverName: 'Rafael Mendes', driverCpf: '22233344455', driverId: '6', plate: 'YZA-4444', inertiaPercentage: 52.3, motorLoadPercentage: 55.2, inertiaPrize: 80, engineLoadPrize: 95, odometro: 2100, fleetName: 'Frota 3' },
    { position: 2, driverName: 'Bruno Ferreira', driverCpf: '33344455566', driverId: '7', plate: 'BCD-5555', inertiaPercentage: 58.7, motorLoadPercentage: 62.8, inertiaPrize: 120, engineLoadPrize: 160, odometro: 2450, fleetName: 'Frota 2' },
    { position: 3, driverName: 'Diego Rocha', driverCpf: '44455566677', driverId: '8', plate: 'EFG-6666', inertiaPercentage: 61.2, motorLoadPercentage: 63.5, inertiaPrize: 150, engineLoadPrize: 175, odometro: 2680, fleetName: 'Frota 1' },
    { position: 4, driverName: 'Thiago Nunes', driverCpf: '55566677788', driverId: '9', plate: 'HIJ-7777', inertiaPercentage: 64.5, motorLoadPercentage: 66.1, inertiaPrize: 180, engineLoadPrize: 195, odometro: 2890, fleetName: 'Frota 3' },
    { position: 5, driverName: 'Gabriel Dias', driverCpf: '66677788899', driverId: '10', plate: 'KLM-8888', inertiaPercentage: 67.1, motorLoadPercentage: 68.4, inertiaPrize: 210, engineLoadPrize: 225, odometro: 3010, fleetName: 'Frota 2' },
  ],
  topDriversEngineLoad: [
    { position: 1, driverName: 'Roberto Alves', driverCpf: '11122233344', driverId: '11', plate: 'PQR-1111', inertiaPercentage: 88.5, motorLoadPercentage: 96.1, inertiaPrize: 620, engineLoadPrize: 920, odometro: 4200, fleetName: 'Frota 1' },
    { position: 2, driverName: 'Carlos Silva', driverCpf: '12345678900', driverId: '1', plate: 'ABC-1234', inertiaPercentage: 94.2, motorLoadPercentage: 93.4, inertiaPrize: 850, engineLoadPrize: 780, odometro: 4520, fleetName: 'Frota 1' },
    { position: 3, driverName: 'Fernando Costa', driverCpf: '55566677788', driverId: '12', plate: 'STU-2222', inertiaPercentage: 86.3, motorLoadPercentage: 91.7, inertiaPrize: 540, engineLoadPrize: 690, odometro: 3950, fleetName: 'Frota 2' },
    { position: 4, driverName: 'José Santos', driverCpf: '98765432100', driverId: '2', plate: 'DEF-5678', inertiaPercentage: 91.8, motorLoadPercentage: 89.2, inertiaPrize: 720, engineLoadPrize: 610, odometro: 3890, fleetName: 'Frota 1' },
    { position: 5, driverName: 'Lucas Pereira', driverCpf: '99988877766', driverId: '13', plate: 'VWX-3333', inertiaPercentage: 83.2, motorLoadPercentage: 87.8, inertiaPrize: 480, engineLoadPrize: 550, odometro: 3450, fleetName: 'Frota 3' },
  ],
  worstDriversEngineLoad: [
    { position: 1, driverName: 'Vinicius Reis', driverCpf: '77788899900', driverId: '14', plate: 'NOP-9999', inertiaPercentage: 54.1, motorLoadPercentage: 48.9, inertiaPrize: 70, engineLoadPrize: 60, odometro: 1980, fleetName: 'Frota 3' },
    { position: 2, driverName: 'Rafael Mendes', driverCpf: '22233344455', driverId: '6', plate: 'YZA-4444', inertiaPercentage: 52.3, motorLoadPercentage: 55.2, inertiaPrize: 80, engineLoadPrize: 95, odometro: 2100, fleetName: 'Frota 3' },
    { position: 3, driverName: 'Eduardo Martins', driverCpf: '88899900011', driverId: '15', plate: 'QRS-0000', inertiaPercentage: 57.8, motorLoadPercentage: 59.4, inertiaPrize: 110, engineLoadPrize: 130, odometro: 2350, fleetName: 'Frota 2' },
    { position: 4, driverName: 'Bruno Ferreira', driverCpf: '33344455566', driverId: '7', plate: 'BCD-5555', inertiaPercentage: 58.7, motorLoadPercentage: 62.8, inertiaPrize: 120, engineLoadPrize: 160, odometro: 2450, fleetName: 'Frota 2' },
    { position: 5, driverName: 'Henrique Lopes', driverCpf: '99900011122', driverId: '16', plate: 'TUV-1111', inertiaPercentage: 63.4, motorLoadPercentage: 66.3, inertiaPrize: 170, engineLoadPrize: 195, odometro: 2780, fleetName: 'Frota 1' },
  ],
  summary: {
    totalDrivers: 48,
    averageInertia: 78.4,
    averageMotorLoad: 82.1,
    totalOdometro: 184520,
    totalInertiaPrize: 8250,
    totalEngineLoadPrize: 4200,
  },
  evolution: [
    { date: '2025-01-01', dateFormatted: '01/01', averageInertia: 74.2, averageMotorLoad: 79.1 },
    { date: '2025-01-05', dateFormatted: '05/01', averageInertia: 76.1, averageMotorLoad: 80.3 },
    { date: '2025-01-10', dateFormatted: '10/01', averageInertia: 75.8, averageMotorLoad: 81.2 },
    { date: '2025-01-15', dateFormatted: '15/01', averageInertia: 77.9, averageMotorLoad: 80.8 },
    { date: '2025-01-20', dateFormatted: '20/01', averageInertia: 79.2, averageMotorLoad: 82.4 },
    { date: '2025-01-25', dateFormatted: '25/01', averageInertia: 77.5, averageMotorLoad: 83.1 },
    { date: '2025-01-30', dateFormatted: '30/01', averageInertia: 78.4, averageMotorLoad: 82.1 },
  ]
});

